import { MapUtils } from '../uitls/mapUtils.js';
/**
 * Utilitaires pour le calcul et le formatage des distances et durées
 */

/**
 * Extrait la valeur numérique d'un texte de distance
 * @param {string} distanceText - Texte de distance (ex: "12.5 km")
 * @returns {number} - Distance en kilomètres
 */
export function parseDistance(distanceText) {
  const match = distanceText.match(/[\d.]+/);
  return match ? parseFloat(match[0]) : 0;
}

/**
 * Extrait la valeur numérique d'un texte de durée
 * @param {string} durationText - Texte de durée (ex: "25 mins" ou "1h 30min")
 * @returns {number} - Durée en minutes
 */
export function parseDuration(durationText) {
  // Gérer les formats comme "1h 30min" ou "45 mins"
  const hourMatch = durationText.match(/(\d+)\s*(?:heure|hour|h)/i);
  const minuteMatch = durationText.match(/(\d+)\s*(?:minute|min|m)/i);

  let totalMinutes = 0;

  if (hourMatch) {
    totalMinutes += parseInt(hourMatch[1]) * 60;
  }

  if (minuteMatch) {
    totalMinutes += parseInt(minuteMatch[1]);
  }

  // Si aucun format n'est trouvé, essayer un format simple
  if (totalMinutes === 0) {
    const simpleMatch = durationText.match(/(\d+)/);
    if (simpleMatch) {
      totalMinutes = parseInt(simpleMatch[1]);
    }
  }

  return totalMinutes;
}

/**
 * Formate une distance en kilomètres ou mètres
 * @param {number} distanceInKm - Distance en kilomètres
 * @returns {string} - Distance formatée
 */
export function formatDistance(distanceInKm) {
  if (distanceInKm < 1) {
    return `${Math.round(distanceInKm * 1000)} m`;
  }
  return `${distanceInKm.toFixed(1)} km`;
}

/**
 * Formate une durée en minutes ou heures+minutes
 * @param {number} durationInMinutes - Durée en minutes
 * @returns {string} - Durée formatée
 */
export function formatDuration(durationInMinutes) {
  if (durationInMinutes < 60) {
    return `${Math.round(durationInMinutes)} min`;
  }
  const hours = Math.floor(durationInMinutes / 60);
  const minutes = Math.round(durationInMinutes % 60);
  return `${hours}h ${minutes}min`;
}

/**
 * Calcule la distance totale et la durée totale à partir des données de route
 * @param {Array} routeData - Données de route retournées par l'API
 * @returns {Object} - {distance: string, duration: string, distanceKm: number, durationMin: number}
 */
export function calculateRouteTotals(routeData) {
  if (!routeData || routeData.length === 0) {
    return {
      distance: '0 m',
      duration: '0 min',
      distanceKm: 0,
      durationMin: 0
    };
  }

  let totalDistance = 0;
  let totalDuration = 0;

  routeData.forEach((segment) => {
    if (segment.distance) {
      totalDistance += parseDistance(segment.distance);
    }
    if (segment.duration) {
      totalDuration += parseDuration(segment.duration);
    }
  });

  return {
    distance: formatDistance(totalDistance),
    duration: formatDuration(totalDuration),
    distanceKm: totalDistance,
    durationMin: totalDuration
  };
}

/**
 * Calcule la distance à vol d'oiseau entre deux points (formule Haversine)
 * @param {{lat:number, lng:number}} point1
 * @param {{lat:number, lng:number}} point2
 * @returns {number} - Distance en kilomètres
 */
export function calculateDirectDistance(point1, point2) {
  const R = 6371; // Rayon de la Terre en km
  const dLat = toRad(point2.lat - point1.lat);
  const dLng = toRad(point2.lng - point1.lng);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(point1.lat)) *
      Math.cos(toRad(point2.lat)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(deg) {
  return deg * (Math.PI / 180);
}

/**
 * Récupère la distance et la durée d'un trajet entre deux points
 * @param {{lat:number, lng:number}} start - Point de départ
 * @param {{lat:number, lng:number}} end - Point d'arrivée
 * @param {{lat:number, lng:number}[]} checkpoints - Points de passage intermédiaires (optionnel)
 * @param {{returnPath:boolean}} options - Options: returnPath pour inclure le chemin détaillé
 * @returns {Promise<{distance:string, distanceKm:number, duration:string, durationMin:number, path?:Array>}>}
 *
 * @example
 * // Distance simple
 * const result = await getRouteDistance(
 *   { lat: 6.37, lng: 2.39 },
 *   { lat: 6.40, lng: 2.42 }
 * )
 * // { distance: "5.2 km", distanceKm: 5.2, duration: "12 min", durationMin: 12 }
 *
 * @example
 * // Avec checkpoints et path
 * const result = await getRouteDistance(
 *   { lat: 6.37, lng: 2.39 },
 *   { lat: 6.40, lng: 2.42 },
 *   [{ lat: 6.38, lng: 2.40 }],
 *   { returnPath: true }
 * )
 * // { distance: "5.2 km", ..., path: [[{lat, lng}, ...]] }
 */
export async function getRouteDistance(
  start,
  end,
  checkpoints = [],
  options = {}
) {
  const routeData = await MapUtils.traceRoute(start, end, checkpoints, true);

  if (!routeData || routeData.length === 0) {
    return {
      distance: '0 m',
      distanceKm: 0,
      duration: '0 min',
      durationMin: 0,
      ...(options.returnPath && { path: [] })
    };
  }

  const totals = calculateRouteTotals(routeData);

  const result = {
    distance: totals.distance,
    distanceKm: totals.distanceKm,
    duration: totals.duration,
    durationMin: totals.durationMin
  };

  if (options.returnPath) {
    result.path = routeData.map((leg) => {
      const path = [];
      leg.steps.forEach((step) => {
        path.push(...step.detailSteps);
      });
      return path;
    });
  }

  return result;
}
